/* eslint-disable jsx-a11y/no-noninteractive-element-interactions */
import 'react-pdf/dist/Page/TextLayer.css';
import 'react-pdf/dist/Page/AnnotationLayer.css';

import { getPrimaryColorGrad1 } from '@brands/Utils/getPrimaryColor';
import InsertDriveFileIcon from '@mui/icons-material/InsertDriveFileTwoTone';
import linkifyHtml from 'linkify-html';
import lozad from 'lozad';
import React, { createRef, useEffect, useMemo, useState } from 'react';
import { GoCircleSlash } from 'react-icons/go';
import { ImArrowDown2 } from 'react-icons/im';
import { Document, Page, pdfjs } from 'react-pdf';
import ReactViewer from 'react-viewer';
import sanitizeHtml from 'sanitize-html';

import useConversationMedia, { IConversationMedia } from '../../../hooks/conversation-hooks/useConversationMedia';
import { IGMMessage } from '../../../services/communication/types/IGMMessage';
import { truncateString } from '../../../Utils/truncateString';
import pdf from '../../assets/pdf.svg';
import styles from './styles.module.scss';
import VideoPlayer from './VideoPlayer';

pdfjs.GlobalWorkerOptions.workerSrc = `https://unpkg.com/pdfjs-dist@${pdfjs.version}/build/pdf.worker.min.mjs`;

interface Props {
  message: IGMMessage;
  mediaIDs: string[];
  messageIndex: number;
  handleMediaLoad: (index: number) => void;
  direction: 'outgoing' | 'incoming';
}

const MessageContent: React.FC<Props> = ({ message, mediaIDs, messageIndex, handleMediaLoad, direction }) => {
  const uploadedMedias: IConversationMedia[] = useConversationMedia(mediaIDs, message.conversation_sid);
  const linkRef = createRef<HTMLAnchorElement>();
  const [, setNumPages] = useState(0);
  const [download, setDownload] = useState<boolean>(false);
  const [showModal, setShowModal] = useState(false);
  const [selectedImageIndex, setSelectedImageIndex] = useState<number>(0);

  const onDocumentLoadSuccess = ({ numPages: numPagesNew }: any): void => {
    setNumPages(numPagesNew);
    setDownload(true);
    handleMediaLoad(messageIndex);
  };

  const MAX_CHARS = 15;

  const handleView = (mediaUrl: string): void => {
    const newTab = window.open(mediaUrl, '_blank');
    if (newTab) newTab.focus();
  };

  const imageMedias = useMemo(() => uploadedMedias.filter((media) => media.mediaType === 'image'), [uploadedMedias]);

  const imageViewerImages = useMemo(() => imageMedias.map((media) => ({ src: media.url })), [imageMedias]);

  const handleImageView = (media: IConversationMedia): void => {
    const imageIndex = imageMedias.findIndex((m) => m.url === media.url);
    if (imageIndex >= 0) {
      setSelectedImageIndex(imageIndex);
      setShowModal(true);
    } else {
      console.warn('Image URL not found in imageMedias');
    }
  };

  // Sanitize and linkify message body
  const withLinks = linkifyHtml(message.body || '', {
    defaultProtocol: 'https',
    attributes: {
      rel: 'noopener noreferrer',
      target: '_blank',
      style: `color: ${direction === 'incoming' ? getPrimaryColorGrad1() : 'white'}`,
    },
  });

  const sanitizedHtml = sanitizeHtml(withLinks, {
    allowedTags: ['p', 'strong', 'em', 'u', 'ol', 'ul', 'li', 'br', 'span', 'a'],
    allowedAttributes: {
      a: ['href', 'target', 'rel'],
      span: ['class'],
      li: ['data-list'],
    },
    allowedClasses: {
      span: ['ql-ui'],
    },
    transformTags: {
      a: (tagName, attribs) => ({
        tagName: 'a',
        attribs: {
          ...attribs,
          target: '_blank',
          rel: 'noopener noreferrer',
          style: `color: ${direction === 'incoming' ? getPrimaryColorGrad1() : 'white'}`,
        },
      }),
    },
  });

  useEffect(() => {
    const observer = lozad(`.${styles.lozad}`);
    observer.observe();
  }, []);

  const handleDownload = async (uploadedMedia: IConversationMedia): Promise<void> => {
    const response = await fetch(uploadedMedia.url);
    const blob = await response.blob();

    const link = document.createElement('a');
    link.href = URL.createObjectURL(blob);
    link.download = decodeURIComponent(uploadedMedia.url.split('?')[0].split('/').pop() ?? '').replace(
      /^[0-9a-fA-F\-]{36}-/,
      '',
    );

    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  return (
    <>
      {message.body === null || message.attributes.archived_at !== undefined ? (
        <div className={`fs.exclude ${styles.deletedMessage}`}>
          <GoCircleSlash className={styles.deletedMessageIcon} /> This message was deleted
        </div>
      ) : (
        <div className="fs.exclude" dangerouslySetInnerHTML={{ __html: sanitizedHtml }} />
      )}

      <div className={styles.mediaWrap}>
        {message.archived_at === null &&
          message.body !== null &&
          message.attributes.archived_at === undefined &&
          uploadedMedias[0] &&
          uploadedMedias.map((uploadedMedia) => (
            <React.Fragment key={uploadedMedia.url}>
              {uploadedMedia.mediaType === 'image' && (
                <img
                  alt="Uploaded"
                  onClick={() => handleImageView(uploadedMedia)}
                  onLoad={() => handleMediaLoad(messageIndex)}
                  className={`fs.exclude ${styles.uploadedImg} ${styles.lozad}`}
                  src={uploadedMedia.url}
                  style={{ cursor: 'pointer' }}
                />
              )}
              {uploadedMedia.mediaType === 'video' && <VideoPlayer videoUrl={uploadedMedia.url} />}
              {uploadedMedia.mediaType === 'other' && (
                <div style={{ width: '100%' }}>
                  {decodeURIComponent(uploadedMedia.url).toLowerCase().includes('.pdf') ? (
                    <div
                      className={styles.pdfDiv}
                      onClick={() => handleView(uploadedMedia.url)}
                      style={{ cursor: 'pointer' }}
                    >
                      <Document
                        file={uploadedMedia.url}
                        onLoadSuccess={onDocumentLoadSuccess}
                        className={`fs.exclude ${styles.lozad} ${styles.pdfDocument}`}
                      >
                        <Page className={styles.pdfPage} pageNumber={1} scale={1} />
                      </Document>
                      {download && (
                        <div className={styles.controls}>
                          <img className={styles.pdfIcon} alt="PDF" src={pdf} />
                          <span className={styles.pdfName}>
                            {truncateString(
                              decodeURIComponent(
                                uploadedMedia.url
                                  .split('?')[0]
                                  .split('/')
                                  .pop()
                                  ?.split('-')
                                  ?.pop()
                                  ?.replace(/%[0-9A-Fa-f]{2}/g, (match) =>
                                    String.fromCharCode(parseInt(match.substr(1), 16)),
                                  ) ?? '',
                              ),
                              MAX_CHARS,
                            )}
                          </span>
                          <span className={styles.pdfDownload}>
                            <a ref={linkRef} onClick={() => handleDownload(uploadedMedia)}>
                              <ImArrowDown2 />
                            </a>
                          </span>
                        </div>
                      )}
                    </div>
                  ) : (
                    <div className={styles.otherFileRow}>
                      <InsertDriveFileIcon
                        sx={{
                          color: direction === 'incoming' ? getPrimaryColorGrad1() : 'white',
                        }}
                      />
                      <span className={styles.fileName}>
                        {truncateString(
                          decodeURIComponent(uploadedMedia.url.split('?')[0].split('/').pop() ?? '').replace(
                            /^[0-9a-fA-F\-]{36}-/,
                            '',
                          ),
                          MAX_CHARS,
                        )}
                      </span>{' '}
                      <span className={styles.fileDownload}>
                        <button
                          onClick={() => handleDownload(uploadedMedia)}
                          style={{
                            color: direction === 'incoming' ? getPrimaryColorGrad1() : 'white',
                            background: 'none',
                            border: 'none',
                            cursor: 'pointer',
                          }}
                          type="button"
                        >
                          Download
                        </button>
                      </span>
                    </div>
                  )}
                </div>
              )}
            </React.Fragment>
          ))}
        <ReactViewer
          visible={showModal}
          onClose={() => setShowModal(false)}
          images={imageViewerImages}
          activeIndex={selectedImageIndex}
          loop
          changeable
          zoomable
          scalable
          rotatable
          downloadable={false}
          attribute={false}
        />
      </div>
    </>
  );
};

export default MessageContent;
